import { useCallback, useEffect, useMemo, useState } from "react";
import { Mail, Search, RefreshCw, AlertTriangle, CheckCircle2, Trash2, ShieldCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { EmailPreviewModal } from "./EmailPreviewModal";

const CATEGORY_META = {
  milestone:                  { label: "Milestone",         accent: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  monthly_report:             { label: "Monthly report",    accent: "bg-blue-50 text-blue-700 border-blue-200" },
  alert:                      { label: "Alert",             accent: "bg-red-50 text-red-700 border-red-200" },
  admin_alert:                { label: "Admin alert",       accent: "bg-orange-50 text-orange-700 border-orange-200" },
  trial_reminder:             { label: "Trial reminder",    accent: "bg-amber-50 text-amber-700 border-amber-200" },
  trial_day3:                 { label: "Trial · Day 3 check-in", accent: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  trial_day6:                 { label: "Trial · Day 6 summary",  accent: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  password_reset:             { label: "Password reset",    accent: "bg-charcoal-100 text-charcoal-700 border-charcoal-200" },
  upgrade_confirmation:       { label: "Upgrade",           accent: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancellation_confirmation:  { label: "Cancellation",      accent: "bg-charcoal-100 text-charcoal-700 border-charcoal-200" },
  team_invite:                { label: "Team invite",       accent: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  owner_digest:               { label: "Owner digest",      accent: "bg-purple-50 text-purple-700 border-purple-200" },
  contact_form:               { label: "Contact form",      accent: "bg-charcoal-100 text-charcoal-700 border-charcoal-200" },
  lead_notification:          { label: "New lead",          accent: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  free_scan_report:           { label: "Free-scan report",  accent: "bg-blue-50 text-blue-700 border-blue-200" },
  free_scan_followup:         { label: "Free-scan follow-up", accent: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  first_hour_checkin:         { label: "First-hour check-in", accent: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  resend:                     { label: "Resend",            accent: "bg-blue-50 text-blue-700 border-blue-200" },
  other:                      { label: "Other",             accent: "bg-charcoal-100 text-charcoal-700 border-charcoal-200" },
};

const PAGE_SIZE = 25;

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return "—"; }
};

export function EmailLogPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [countsByCategory, setCountsByCategory] = useState({});
  const [skip, setSkip] = useState(0);
  const [category, setCategory] = useState("");
  const [toFilter, setToFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip };
      if (category) params.category = category;
      if (toFilter.trim()) params.to = toFilter.trim().toLowerCase();
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/admin/email-log", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setCountsByCategory(data.counts_by_category || {});
    } catch (err) {
      // 403 handled by parent (owner check); silently swallow other issues here.
    } finally {
      setLoading(false);
    }
  }, [skip, category, toFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Reset paging when a filter changes.
  useEffect(() => { setSkip(0); }, [category, toFilter, statusFilter]);

  const orderedCategories = useMemo(() => {
    const known = Object.keys(CATEGORY_META);
    // put known ones first (in declaration order), then any others.
    const rest = Object.keys(countsByCategory).filter((k) => !known.includes(k));
    return [...known.filter((k) => countsByCategory[k]), ...rest];
  }, [countsByCategory]);

  const pageStart = skip + 1;
  const pageEnd = Math.min(skip + items.length, total);
  const hasPrev = skip > 0;
  const hasNext = skip + items.length < total;

  // Iter-56d — Acknowledge / un-acknowledge a failed delivery. Acknowledged
  // rows stay in the log (audit) but stop counting toward the top banner.
  // Optimistic update — the row's chip flips instantly, roll back on error.
  const acknowledgeRow = async (id, on) => {
    if (!id) return;
    const prev = items;
    setItems((cur) => cur.map((it) => it.id === id ? {
      ...it,
      acknowledged_at: on ? new Date().toISOString() : null,
      acknowledged_by: on ? "you" : null,
    } : it));
    try {
      await api.post(
        `/admin/email-log/${id}/${on ? "acknowledge" : "unacknowledge"}`,
        {});
      toast.success(on ? "Failure acknowledged — banner will clear on next refresh." : "Un-acknowledged.");
    } catch (err) {
      setItems(prev);
      toast.error("Could not update this entry.");
    }
  };

  // Owner-only: delete a single row. Optimistic — remove from `items` before
  // the API returns so the UI feels snappy; roll back on error.
  const deleteRow = async (id) => {
    if (!id) return;
    if (!window.confirm("Delete this email log entry? This can't be undone.")) return;
    setDeletingId(id);
    const prev = items;
    setItems((cur) => cur.filter((it) => it.id !== id));
    try {
      await api.delete(`/admin/email-log/${id}`);
      setTotal((t) => Math.max(0, t - 1));
      toast.success("Email log entry deleted.");
    } catch (err) {
      setItems(prev);
      toast.error("Could not delete that entry.");
    } finally {
      setDeletingId(null);
    }
  };

  // Owner-only bulk-cleanup: delete every row matching the CURRENT filters.
  // Server enforces at-least-one-filter, so an unfiltered click is a no-op.
  const deleteMatchingFilter = async () => {
    const filterDesc = [];
    if (statusFilter) filterDesc.push(`status=${statusFilter}`);
    if (category) filterDesc.push(`category=${category}`);
    if (toFilter.trim()) filterDesc.push(`recipient contains "${toFilter.trim()}"`);
    if (!filterDesc.length) {
      toast.error("Set a filter first (status, category, or recipient) before bulk-deleting.");
      return;
    }
    if (!window.confirm(
      `Delete every log entry matching ${filterDesc.join(" + ")}?\n\nThis can't be undone.`
    )) return;
    try {
      const payload = {};
      if (statusFilter) payload.status = statusFilter;
      if (category) payload.category = category;
      if (toFilter.trim()) payload.to_contains = toFilter.trim();
      const { data } = await api.post("/admin/email-log/bulk-delete", payload);
      toast.success(`Deleted ${data.deleted} entr${data.deleted === 1 ? "y" : "ies"}.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed.");
    }
  };

  return (
    <section
      id="email-log"
      className="mt-8 rounded-2xl bg-white border border-black/5 shadow-soft overflow-hidden"
      data-testid="email-log-card"
    >
      <header className="px-5 py-4 border-b border-black/5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-charcoal-900 grid place-items-center">
            <Mail className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-charcoal-900 tracking-tight">Emails sent</h2>
            <p className="text-xs text-charcoal-500">
              Every outbound Mission Watch email — {total.toLocaleString()} total
            </p>
          </div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-black/10 hover:bg-charcoal-50 transition-colors"
          data-testid="email-log-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </header>

      {/* Filters */}
      <div className="px-5 py-3 border-b border-black/5 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setCategory("")}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
            category === ""
              ? "bg-charcoal-900 text-white border-charcoal-900"
              : "bg-white text-charcoal-700 border-black/10 hover:bg-charcoal-50"
          }`}
          data-testid="email-log-filter-all"
        >
          All
        </button>
        {orderedCategories.map((k) => {
          const meta = CATEGORY_META[k] || { label: k, accent: CATEGORY_META.other.accent };
          const active = category === k;
          return (
            <button
              key={k}
              onClick={() => setCategory(k)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                active
                  ? "bg-charcoal-900 text-white border-charcoal-900"
                  : `${meta.accent} hover:brightness-95`
              }`}
              data-testid={`email-log-filter-${k}`}
            >
              {meta.label}
              <span className={`ml-1.5 tabular-nums ${active ? "text-white/60" : "opacity-60"}`}>
                {countsByCategory[k] ?? 0}
              </span>
            </button>
          );
        })}

        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-charcoal-400" />
            <input
              value={toFilter}
              onChange={(e) => setToFilter(e.target.value)}
              placeholder="filter by recipient email"
              className="pl-8 pr-3 py-1.5 text-xs rounded-full border border-black/10 bg-white w-56 focus:outline-none focus:ring-1 focus:ring-charcoal-300"
              data-testid="email-log-filter-to"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs px-2.5 py-1.5 rounded-full border border-black/10 bg-white"
            data-testid="email-log-filter-status"
          >
            <option value="">Any status</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
          {(statusFilter || category || toFilter.trim()) && (
            <button
              type="button"
              onClick={deleteMatchingFilter}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
              data-testid="email-log-bulk-delete"
              title="Delete every entry matching the current filters"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete matching
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="email-log-table">
          <thead className="bg-charcoal-50 text-charcoal-500">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">When</th>
              <th className="text-left font-medium px-4 py-2.5">Recipient</th>
              <th className="text-left font-medium px-4 py-2.5">Category</th>
              <th className="text-left font-medium px-4 py-2.5">Subject & reason</th>
              <th className="text-left font-medium px-4 py-2.5">Status</th>
              <th className="text-right font-medium px-4 py-2.5 w-16">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-charcoal-500">
                  {loading ? "Loading…" : "No emails match your filters."}
                </td>
              </tr>
            )}
            {items.map((it) => {
              const meta = CATEGORY_META[it.category] || {
                label: it.category, accent: CATEGORY_META.other.accent,
              };
              const failed = it.status === "failed";
              return (
                <tr
                  key={it.id}
                  onClick={() => setPreviewId(it.id)}
                  className="hover:bg-charcoal-50 cursor-pointer transition-colors"
                  data-testid={`email-log-row-${it.id}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-charcoal-500">
                    {fmtWhen(it.sent_at)}
                  </td>
                  <td className="px-4 py-3 text-charcoal-800">
                    <div className="font-medium">{it.to || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-full border ${meta.accent}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-charcoal-800 max-w-xl">
                    <div className="font-medium truncate">{it.subject || "—"}</div>
                    {it.reason && (
                      <div className="text-xs text-charcoal-500 mt-0.5 truncate">{it.reason}</div>
                    )}
                    {failed && it.error && (
                      <div className="text-xs text-red-600 mt-0.5 truncate font-mono">
                        {it.error}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {failed ? (
                      <div className="flex flex-col gap-1">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full w-fit">
                          <AlertTriangle className="w-3 h-3" /> Failed
                        </span>
                        {it.acknowledged_at && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full w-fit"
                            title={`Acknowledged ${fmtWhen(it.acknowledged_at)}${it.acknowledged_by ? ` by ${it.acknowledged_by}` : ""}`}
                            data-testid={`email-log-acked-chip-${it.id}`}
                          >
                            <ShieldCheck className="w-2.5 h-2.5" /> Acknowledged
                          </span>
                        )}
                      </div>
                    ) : it.status === "sent" ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> Sent
                      </span>
                    ) : (
                      <span className="text-xs text-charcoal-500">{it.status || "—"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="inline-flex items-center gap-1">
                      {/* Iter-56d — Acknowledge control on failed rows only.
                          Acknowledged failures stay in the log for audit but
                          stop counting toward the top "needs attention" banner.
                          Unacknowledged failures show a green shield to mark
                          them reviewed; already-acknowledged rows show a
                          rewind icon to undo. */}
                      {failed && (
                        it.acknowledged_at ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); acknowledgeRow(it.id, false); }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors"
                            title={`Acknowledged${it.acknowledged_by ? ` by ${it.acknowledged_by}` : ""} · click to undo`}
                            data-testid={`email-log-unacknowledge-${it.id}`}
                            aria-label="Un-acknowledge this failed delivery"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); acknowledgeRow(it.id, true); }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-charcoal-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                            title="Acknowledge this failed delivery (keeps it in the log, removes it from the banner)"
                            data-testid={`email-log-acknowledge-${it.id}`}
                            aria-label="Acknowledge this failed delivery"
                          >
                            <ShieldCheck className="w-3.5 h-3.5" />
                          </button>
                        )
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deleteRow(it.id); }}
                        disabled={deletingId === it.id}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-charcoal-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                        title="Delete this entry"
                        data-testid={`email-log-delete-${it.id}`}
                        aria-label="Delete email log entry"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-5 py-3 border-t border-black/5 flex items-center justify-between text-xs text-charcoal-500">
        <span data-testid="email-log-pagination-info">
          {total > 0 ? `${pageStart.toLocaleString()}–${pageEnd.toLocaleString()} of ${total.toLocaleString()}` : "0 emails"}
        </span>
        <div className="flex items-center gap-2">
          <button
            disabled={!hasPrev}
            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            className="px-3 py-1.5 rounded-full border border-black/10 hover:bg-charcoal-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="email-log-prev"
          >
            Previous
          </button>
          <button
            disabled={!hasNext}
            onClick={() => setSkip(skip + PAGE_SIZE)}
            className="px-3 py-1.5 rounded-full border border-black/10 hover:bg-charcoal-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="email-log-next"
          >
            Next
          </button>
        </div>
      </div>

      <EmailPreviewModal
        emailId={previewId}
        open={!!previewId}
        onClose={() => setPreviewId(null)}
      />
    </section>
  );
}
